import type { HistoryItem, MemberRecord, TitleCard } from './api'

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/** History grouped by calendar month, in the order given (newest first). */
export function groupByMonth(items: HistoryItem[]): { month: string; items: HistoryItem[] }[] {
  const out: { month: string; items: HistoryItem[] }[] = []
  for (const it of items) {
    const d = new Date(it.at)
    const month = `${MONTHS[d.getMonth()]} ${d.getFullYear()}`
    const last = out[out.length - 1]
    if (last && last.month === month) last.items.push(it)
    else out.push({ month, items: [it] })
  }
  return out
}

export type CardAction = 'end-date' | 'pay'

/** An expired title can have its end date moved too: moving it past today
 *  restores it (operator decision 2026-10-05: lapsed means expired). On Hold,
 *  Bad Address, Other, cancelled and never-had titles are paid for, not moved.
 *  SimpleCirc owns its own rows. */
export function cardActions(card: TitleCard): CardAction[] {
  if (card.sc_owned) return []
  if (card.state === 'active') return ['end-date', 'pay']
  if (card.state === 'lapsed' && card.status === 'expired') return ['end-date', 'pay']
  return ['pay']
}

/** The record has an address and it is not suppressed (the api tags suppression). */
export function canResend(r: MemberRecord): boolean {
  return !!r.email && !r.email_suppressed && !(r.tags ?? []).includes('Email suppressed')
}

export const dollars = (cents: number): string => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export function fmtMonth(d: string | null): string {
  const m = d ? /^(\d{4})-(\d{2})/.exec(d) : null
  return m ? `${MONTHS[Number(m[2]) - 1].slice(0, 3)} ${m[1]}` : ''
}

export function fmtDay(iso: string): string {
  const d = new Date(iso)
  return `${MONTHS[d.getMonth()].slice(0, 3)} ${d.getDate()}`
}

/** What is left to refund on a payment: the server's figure, else the whole amount. */
export const refundableCents = (item: { amount_cents: number | null; refundable_cents?: number | null }): number =>
  item.refundable_cents ?? item.amount_cents ?? 0

/** A receipt prints only for a live payment the member paid for (the server 404s a voided one). */
export const canPrintReceipt = (e: { kind: string; order_key: string | null; what: string; is_payer: boolean; status: string }): boolean =>
  e.kind === 'money' && !!e.order_key && e.what === 'Payment' && e.is_payer && e.status !== 'voided'

export const voidValid = ({ reason }: { reason: string }): boolean => reason.trim().length > 0

export type RefundMembership = 'end_now' | 'keep' | 'new_end'

export interface RefundDraft {
  amountCents: number; maxCents: number; reference: string; method: 'offline' | 'card'; reason: string
  /** What happens to the membership; '' until staff choose. */
  membership: RefundMembership | ''
  /** YYYY-MM-DD, used with 'new_end' only. */
  newEnd: string
  /** Today as YYYY-MM-DD: a new end date must come after it. */
  today: string
}

/** Offline refunds also need the number of the refund check we wrote; card refunds do not. */
export function refundValid(d: RefundDraft): boolean {
  if (!Number.isInteger(d.amountCents) || d.amountCents <= 0 || d.amountCents > d.maxCents) return false
  if (d.method === 'offline' && !d.reference.trim()) return false
  if (!d.membership) return false
  if (d.membership === 'new_end' && !(/^\d{4}-\d{2}-\d{2}$/.test(d.newEnd) && d.newEnd > d.today)) return false
  return d.reason.trim().length > 0
}

/** "12.50" -> 1250; anything that is not a plain dollar amount -> NaN. */
export function parseCents(v: string): number {
  const t = v.trim().replace(/^\$/, '')
  return /^\d+(\.\d{1,2})?$/.test(t) ? Math.round(Number(t) * 100) : NaN
}
